import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import charlieCrest from '../assets/icons/charlie-crest.png';
import { THEME_2026 } from '../lib/theme2026';
import { boundedCall, isNetworkFailure } from '../lib/genservBoundedCall';

const PROFILE_KEY = 'charlie_user_profile';

export function isAuthenticated() {
  // We'll check Supabase session on mount — return true optimistically if profile cached
  return !!localStorage.getItem(PROFILE_KEY);
}

export function getCachedProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY)); } catch { return null; }
}

// v1.7.40.61 -- real, shared helper so App.js's background revalidation
// (see the useEffect in App()) writes to the exact same real key via
// the exact same real function LoginGate itself already used, instead
// of a second, hand-typed 'charlie_user_profile' string that could
// silently drift out of sync with this one.
export function setCachedProfile(profile) {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function clearAuth() {
  localStorage.removeItem(PROFILE_KEY);
  supabase.auth.signOut();
}

export default function LoginGate({ onAuth }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true); // start loading to check existing session
  const [error, setError] = useState('');

  // On mount, check if there's already a valid Supabase session.
  // GS-0077: bounded. With one bar the session check or the profile read can hang; the cached profile (this phone's last good sign-in) is used when the profile read does not answer, so the
  // field app opens instead of spinning. A real "no session" or an inactive account still shows the form.
  useEffect(() => {
    let live = true;
    (async () => {
      let session = null;
      try { session = (await boundedCall(supabase.auth.getSession(), 6000))?.data?.session || null; } catch (e) { session = null; }
      if (!live) return;
      if (session?.user) {
        let profile = null;
        let reached = true;
        try {
          const r = await boundedCall(supabase.from('user_profiles').select('*').eq('id', session.user.id).single(), 6000);
          profile = r?.data || null;
          if (r?.error && isNetworkFailure(r.error)) reached = false;
        } catch (e) { reached = false; }
        if (!live) return;
        if (!profile && !reached) {
          const cached = getCachedProfile();
          if (cached && cached.id === session.user.id && cached.is_active) { onAuth(session.user, cached); return; }
        }
        if (profile?.is_active) {
          localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
          onAuth(session.user, profile);
          return;
        }
      }
      setLoading(false);
    })();
    return () => { live = false; };
  }, [onAuth]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const NO_SIGNAL = 'No signal right now. Move to a spot with signal and try again.';
    let signIn;
    try { signIn = await boundedCall(supabase.auth.signInWithPassword({ email, password }), 15000, NO_SIGNAL); } catch (e) {
      setError(e.message); setLoading(false); return;
    }
    const { data, error: err } = signIn;
    if (err && isNetworkFailure(err)) { setError(NO_SIGNAL); setLoading(false); return; }
    if (err) {
      console.warn('[charlie] sign-in failed:', err.message); // eslint-disable-line no-console
      setError(/invalid login credentials/i.test(err.message || '') ? 'Invalid email or password.' : `Could not sign in: ${err.message}`);
      setLoading(false);
      return;
    }

    let profile = null;
    try {
      profile = (await boundedCall(supabase.from('user_profiles').select('*').eq('id', data.user.id).single(), 15000))?.data || null;
    } catch (e) {
      setError('Signed in, but the signal dropped before your account loaded. Try again.'); setLoading(false); return;
    }

    if (!profile || !profile.is_active) {
      await supabase.auth.signOut();
      setError('Your account is inactive. Contact Shane.');
      setLoading(false);
      return;
    }

    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    onAuth(data.user, profile);
  }

  // #2051 -- real fix: this screen was still on the pre-#2041 navy/gold
  // treatment (#0f1423/#1e2433/#c9a227 hardcoded), never brought into
  // the slate rebase or #2045's loading-screen redesign. Palette-and-
  // token pass only, per Goose's explicit scope -- same structure
  // (logo, "Sign in", email, password, button, footer line), every
  // color now resolves through THEME_2026 instead of a hardcoded hex.
  // Background uses the same lapelGradient #2045 uses for the loading
  // screen -- both are pre-app "first impression" surfaces, using the
  // identical treatment is real cohesion, not a new palette.
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: THEME_2026.lapelGradient }}>
        <div style={{ fontSize: 13, color: 'rgba(241,245,249,0.55)', letterSpacing: '0.2em' }}>C.H.A.R.L.I.E.</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: THEME_2026.lapelGradient, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    }}>
      <div style={{ width: '100%', maxWidth: 380, padding: '0 24px' }}>

        {/* Logo -- real, 2026-08-07: added the real hexagon C mark
            (matches the new load screen + About page). This screen
            previously had no real logo image, just plain text. */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <img src={charlieCrest} alt="CHARLIE" style={{ width: 72, height: 'auto', marginBottom: 14 }} />
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--gov-slate-100)', letterSpacing: '0.2em', marginBottom: 6 }}>
            C.H.A.R.L.I.E.
          </div>
          <div style={{ fontSize: 12, color: 'rgba(241,245,249,0.55)', letterSpacing: '0.1em' }}>WELL REPD CONSULTING</div>
        </div>

        {/* Card */}
        <div style={{ background: THEME_2026.cardSidebar, borderRadius: 16, padding: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--gov-slate-100)', marginBottom: 4 }}>Sign in</div>
          <div style={{ fontSize: 13, color: 'rgba(241,245,249,0.5)', marginBottom: 24 }}>Use your wellrepd.com email</div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="login-email" style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(241,245,249,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Email</label>
              <input
                id="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@wellrepd.com" required autoFocus
                style={{ width: '100%', background: THEME_2026.primaryBg, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, padding: '11px 14px', color: 'var(--gov-slate-100)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label htmlFor="login-password" style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(241,245,249,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Password</label>
              <input
                id="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••" required
                style={{ width: '100%', background: THEME_2026.primaryBg, border: '1px solid rgba(255,255,255,0.14)', borderRadius: 8, padding: '11px 14px', color: 'var(--gov-slate-100)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
              />
            </div>

            {error && (
              <div style={{ background: 'rgba(153,24,44,0.16)', border: `1px solid ${THEME_2026.statusError}66`, color: '#fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              style={{ width: '100%', background: THEME_2026.brassAccent, color: THEME_2026.primaryBg, border: 'none', borderRadius: 8, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              Sign In
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'rgba(241,245,249,0.35)' }}>
          Forgot your password? Contact Shane.
        </div>
      </div>
    </div>
  );
}
